import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { NAV_ITEMS } from "../../data/content";
import { Menu, X, ArrowUpRight } from "lucide-react";

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onCareers = pathname.startsWith("/careers");
  // Hash anchors must be prefixed with "/" when off the landing page so they
  // navigate to the landing page and then scroll.
  const hashHref = (id) => (onCareers ? `/#${id}` : `#${id}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-bone/75 backdrop-blur-xl border-b border-ink/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="/"
          data-testid="brand-logo-link"
          className="group flex items-center gap-2"
        >
          <span className="font-display font-black tracking-tight text-ink text-xl md:text-[22px] leading-none">
            Milestone
          </span>
          <span className="hidden sm:inline-block font-mono text-[10px] tracking-[0.22em] uppercase text-ink-soft mt-1">
            Preservatives
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((n) => (
            <a
              key={n.id}
              href={hashHref(n.id)}
              data-testid={`nav-link-${n.id}`}
              className="text-[13px] font-medium text-ink/70 hover:text-ink transition-colors relative group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-ink transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <a
            href="/careers"
            data-testid="nav-link-careers"
            className={`text-[13px] font-medium transition-colors relative group ${
              onCareers ? "text-ink" : "text-ink/70 hover:text-ink"
            }`}
          >
            Careers
            <span
              className={`absolute -bottom-1 left-0 h-px bg-ink transition-all duration-300 ${
                onCareers ? "w-full" : "w-0 group-hover:w-full"
              }`}
            />
          </a>
        </nav>

        <a
          href={hashHref("contact")}
          data-testid="header-cta-contact"
          className="hidden lg:inline-flex items-center gap-1.5 bg-ink text-bone px-4 py-2.5 text-[13px] font-medium rounded-sm hover:bg-ink/85 transition-all group"
        >
          Get in touch
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>

        <button
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden w-9 h-9 grid place-items-center text-ink"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-ink/10 bg-bone/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((n) => (
              <a
                key={n.id}
                href={hashHref(n.id)}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.id}`}
                className="font-display text-2xl text-ink"
              >
                {n.label}
              </a>
            ))}
            <a
              href="/careers"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-link-careers"
              className="font-display text-2xl text-ink"
            >
              Careers
            </a>
            <a
              href={hashHref("contact")}
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-contact"
              className="mt-2 inline-flex items-center justify-center gap-1.5 bg-ink text-bone px-4 py-3 text-sm rounded-sm"
            >
              Get in touch <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
